"use client";

import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Crop,
  File as FileIcon,
  ImageIcon,
  ImagePlay,
  Link2,
  RotateCcw,
} from "lucide-react";
import { BannerCropModal } from "@/components/revive-create/BannerCropModal";
import { ImageCropModal } from "@/components/revive-create/ImageCropModal";
import { TwoTokensIcon } from "@/components/revive-create/icons/TwoTokensIcon";
import { Input } from "@/components/revive-create/ui/input";
import { Textarea } from "@/components/revive-create/ui/textarea";
import { Card } from "@/components/revive-landing/ui/card";
import {
  BANNER_OUTPUT_HEIGHT,
  BANNER_OUTPUT_WIDTH,
  blobToDataUrl,
  exportBannerCropToJpegBlob,
  exportFullImageToJpegBlob,
  exportLogoCropToJpegBlob,
  isBannerAspectMatching,
  isLogoAspectMatching,
  LOGO_ASPECT,
  LOGO_OUTPUT_SIZE,
} from "@/lib/revive-create/banner-crop-export";
import {
  IMAGE_TOO_LARGE_MESSAGE,
  isImageFileTooLarge,
  MAX_TOKEN_IMAGE_LABEL,
} from "@/lib/token-create/image-upload-limits";

const TOKEN_LOGO_FRAME =
  "relative aspect-square w-full max-w-[144px] shrink-0 overflow-hidden rounded-xl";

export function TokenCreateForm({
  resetKey,
  newToken,
  updateNewToken,
  nameFieldLabel = "Token name",
  nameFieldPlaceholder = "Token Name",
  imageFieldLabel = "Token image",
  assetNoun = "token",
  showBannerDescription = true,
  symbolMaxLength = 12,
}) {
  const [socialOpen, setSocialOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [isBannerDragging, setIsBannerDragging] = useState(false);
  const [bannerCropOpen, setBannerCropOpen] = useState(false);
  const [bannerCropSrc, setBannerCropSrc] = useState(null);
  const [bannerCropMustApply, setBannerCropMustApply] = useState(false);
  const bannerInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [logoCropOpen, setLogoCropOpen] = useState(false);
  const [logoCropSrc, setLogoCropSrc] = useState(null);
  const [logoCropMustApply, setLogoCropMustApply] = useState(false);
  const [logoSizeError, setLogoSizeError] = useState(null);
  const [bannerSizeError, setBannerSizeError] = useState(null);
  const fileInputRef = useRef(null);
  const prevResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;

    setSocialOpen(false);
    updateNewToken({
      socialWebsite: "",
      socialTwitter: "",
      socialTelegram: "",
      image: "",
      previewLogoDataUrl: undefined,
      previewBannerDataUrl: undefined,
    });
    setBannerOpen(false);
    setBannerFile(null);
    setBannerPreview(null);
    setIsBannerDragging(false);
    setBannerCropOpen(false);
    setBannerCropSrc(null);
    setBannerCropMustApply(false);
    setUploadedFile(null);
    setUploadedPreview(null);
    setIsDragging(false);
    setLogoCropOpen(false);
    setLogoCropSrc(null);
    setLogoCropMustApply(false);
    setLogoSizeError(null);
    setBannerSizeError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetKey, updateNewToken]);

  useEffect(() => {
    const w = newToken.socialWebsite?.trim() ?? "";
    const t = newToken.socialTwitter?.trim() ?? "";
    const tg = newToken.socialTelegram?.trim() ?? "";
    if (w || t || tg) setSocialOpen(true);
  }, [newToken.socialWebsite, newToken.socialTwitter, newToken.socialTelegram]);

  // Drop stale local previews when the draft store was reset (e.g. after a successful launch).
  useEffect(() => {
    if (!newToken.previewLogoDataUrl?.trim() && !newToken.image?.trim()) {
      setUploadedPreview(null);
      setUploadedFile(null);
      setLogoCropOpen(false);
      setLogoCropSrc(null);
      setLogoCropMustApply(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    if (!newToken.previewBannerDataUrl?.trim()) {
      setBannerPreview(null);
      setBannerFile(null);
      setBannerCropOpen(false);
      setBannerCropSrc(null);
      setBannerCropMustApply(false);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
    }
  }, [newToken.previewLogoDataUrl, newToken.previewBannerDataUrl, newToken.image]);

  useEffect(() => {
    const logo = newToken.previewLogoDataUrl?.trim();
    if (logo) {
      setUploadedPreview((current) => current ?? logo);
      if (!newToken.image?.trim()) {
        updateNewToken({ image: "uploaded" });
      }
    }
    const banner = newToken.previewBannerDataUrl?.trim();
    if (banner) {
      setBannerPreview((current) => current ?? banner);
      setBannerOpen(true);
    }
  }, [
    newToken.previewBannerDataUrl,
    newToken.previewLogoDataUrl,
    newToken.image,
    updateNewToken,
  ]);

  function handleBannerSelect(file) {
    if (!file.type.startsWith("image/")) return;
    if (isImageFileTooLarge(file)) {
      setBannerSizeError(IMAGE_TOO_LARGE_MESSAGE);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
      return;
    }
    setBannerSizeError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        void (async () => {
          if (isBannerAspectMatching(img.naturalWidth, img.naturalHeight)) {
            try {
              const blob = await exportFullImageToJpegBlob(
                dataUrl,
                BANNER_OUTPUT_WIDTH,
                BANNER_OUTPUT_HEIGHT,
              );
              if (isImageFileTooLarge(blob)) {
                setBannerSizeError(IMAGE_TOO_LARGE_MESSAGE);
                return;
              }
              const jpegDataUrl = await blobToDataUrl(blob);
              const out = new File([blob], "banner.jpg", { type: "image/jpeg" });
              setBannerPreview(jpegDataUrl);
              setBannerFile(out);
              updateNewToken({ previewBannerDataUrl: jpegDataUrl });
              setBannerCropMustApply(false);
              setBannerCropSrc(null);
              setBannerCropOpen(false);
            } catch {
              setBannerSizeError("Could not process banner image. Try another file.");
            }
          } else {
            setBannerCropSrc(dataUrl);
            setBannerCropMustApply(true);
            setBannerCropOpen(true);
          }
        })();
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  async function handleBannerCropApply(pixels) {
    if (!bannerCropSrc) return;
    const blob = await exportBannerCropToJpegBlob(bannerCropSrc, pixels);
    if (isImageFileTooLarge(blob)) {
      setBannerSizeError(IMAGE_TOO_LARGE_MESSAGE);
      return;
    }
    setBannerSizeError(null);
    const out = new File([blob], "banner.jpg", { type: "image/jpeg" });
    const jpegDataUrl = await blobToDataUrl(blob);
    setBannerPreview(jpegDataUrl);
    setBannerFile(out);
    updateNewToken({ previewBannerDataUrl: jpegDataUrl });
    setBannerCropOpen(false);
    setBannerCropSrc(null);
    setBannerCropMustApply(false);
  }

  function closeBannerCropModal() {
    setBannerCropOpen(false);
    setBannerCropSrc(null);
    if (bannerCropMustApply && bannerInputRef.current) {
      bannerInputRef.current.value = "";
    }
    setBannerCropMustApply(false);
  }

  function openBannerAdjustCrop() {
    if (!bannerPreview) return;
    setBannerCropSrc(bannerPreview);
    setBannerCropMustApply(false);
    setBannerCropOpen(true);
  }

  function openLogoAdjustCrop() {
    if (!uploadedPreview) return;
    setLogoCropSrc(uploadedPreview);
    setLogoCropMustApply(false);
    setLogoCropOpen(true);
  }

  function applyLogoSelection(file, previewDataUrl) {
    setUploadedFile(file);
    setUploadedPreview(previewDataUrl);
    updateNewToken({ image: "uploaded", previewLogoDataUrl: previewDataUrl });
  }

  async function handleLogoCropApply(pixels) {
    if (!logoCropSrc) return;
    const blob = await exportLogoCropToJpegBlob(logoCropSrc, pixels);
    if (isImageFileTooLarge(blob)) {
      setLogoSizeError(IMAGE_TOO_LARGE_MESSAGE);
      return;
    }
    setLogoSizeError(null);
    const out = new File([blob], "logo.jpg", { type: "image/jpeg" });
    const jpegDataUrl = await blobToDataUrl(blob);
    applyLogoSelection(out, jpegDataUrl);
    setLogoCropOpen(false);
    setLogoCropSrc(null);
    setLogoCropMustApply(false);
  }

  function closeLogoCropModal() {
    setLogoCropOpen(false);
    setLogoCropSrc(null);
    if (logoCropMustApply && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setLogoCropMustApply(false);
  }

  function resetLogoSelection(e) {
    e?.stopPropagation?.();
    setUploadedFile(null);
    setUploadedPreview(null);
    setLogoSizeError(null);
    updateNewToken({ image: "", previewLogoDataUrl: undefined });
    setLogoCropOpen(false);
    setLogoCropSrc(null);
    setLogoCropMustApply(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function resetBannerSelection(e) {
    e?.stopPropagation?.();
    setBannerFile(null);
    setBannerPreview(null);
    setBannerSizeError(null);
    updateNewToken({ previewBannerDataUrl: undefined });
    setBannerCropOpen(false);
    setBannerCropSrc(null);
    setBannerCropMustApply(false);
    if (bannerInputRef.current) bannerInputRef.current.value = "";
  }

  function handleFileSelect(file) {
    if (!file.type.startsWith("image/")) return;
    if (isImageFileTooLarge(file)) {
      setLogoSizeError(IMAGE_TOO_LARGE_MESSAGE);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setLogoSizeError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        void (async () => {
          if (isLogoAspectMatching(img.naturalWidth, img.naturalHeight)) {
            try {
              const blob = await exportFullImageToJpegBlob(
                dataUrl,
                LOGO_OUTPUT_SIZE,
                LOGO_OUTPUT_SIZE,
              );
              if (isImageFileTooLarge(blob)) {
                setLogoSizeError(IMAGE_TOO_LARGE_MESSAGE);
                return;
              }
              const jpegDataUrl = await blobToDataUrl(blob);
              applyLogoSelection(new File([blob], "logo.jpg", { type: "image/jpeg" }), jpegDataUrl);
              setLogoCropOpen(false);
              setLogoCropSrc(null);
              setLogoCropMustApply(false);
            } catch {
              setLogoSizeError("Could not process logo image. Try another file.");
            }
          } else {
            setLogoCropSrc(dataUrl);
            setLogoCropMustApply(true);
            setLogoCropOpen(true);
          }
        })();
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  return (
    <Card className="space-y-3">
      <div className="flex gap-3">
        <div className="w-1/2 space-y-1.5">
          <p className="text-xs font-medium text-zinc-400">
            {nameFieldLabel} <span className="text-amber-400/90">*</span>
          </p>
          <Input
            placeholder={nameFieldPlaceholder}
            maxLength={64}
            value={newToken.name}
            onChange={(e) => updateNewToken({ name: e.target.value })}
          />
        </div>
        <div className="w-1/2 space-y-1.5">
          <p className="flex items-center justify-between gap-2 text-xs font-medium text-zinc-400">
            <span>
              Symbol <span className="text-amber-400/90">*</span>
            </span>
            <span className="tabular-nums text-zinc-500">
              {(newToken.symbol || "").length}/{symbolMaxLength}
            </span>
          </p>
          <Input
            placeholder="Symbol"
            maxLength={symbolMaxLength}
            value={newToken.symbol}
            onChange={(e) =>
              updateNewToken({
                symbol: e.target.value.toUpperCase().slice(0, symbolMaxLength),
              })
            }
            aria-invalid={newToken.symbol.length > symbolMaxLength}
          />
          {newToken.symbol.length > symbolMaxLength ? (
            <p className="text-xs text-red-400" role="alert">
              Symbol must be {symbolMaxLength} characters or fewer.
            </p>
          ) : (
            <p className="text-xs text-zinc-500">Max {symbolMaxLength} characters.</p>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-zinc-400">
          Description <span className="text-amber-400/90">*</span>
        </p>
        <Textarea
          placeholder="Write a short description"
          value={newToken.description}
          onChange={(e) => updateNewToken({ description: e.target.value })}
        />
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50">
        <button
          type="button"
          onClick={() => setSocialOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:text-zinc-100"
        >
          <Link2 className="h-4 w-4 text-zinc-500" />
          <span>Add social links</span>
          <span className="ml-1 text-xs text-zinc-500">(Optional)</span>
          <span className="ml-auto">
            {socialOpen ? (
              <ChevronUp className="h-4 w-4 text-zinc-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            )}
          </span>
        </button>

        {socialOpen && (
          <div className="space-y-3 border-t border-zinc-800 px-4 pb-4 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-zinc-400">Website</p>
                <Input
                  placeholder="https://example.com"
                  value={newToken.socialWebsite ?? ""}
                  onChange={(e) => updateNewToken({ socialWebsite: e.target.value })}
                  inputMode="url"
                  autoComplete="url"
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-zinc-400">X</p>
                <Input
                  placeholder="https://x.com/…"
                  value={newToken.socialTwitter ?? ""}
                  onChange={(e) => updateNewToken({ socialTwitter: e.target.value })}
                  inputMode="url"
                  autoComplete="url"
                />
              </div>
            </div>
            <div className="w-1/2 space-y-1.5 pr-1.5">
              <p className="text-xs font-medium text-zinc-400">Telegram</p>
                <Input
                  placeholder="https://t.me/…"
                  value={newToken.socialTelegram ?? ""}
                  onChange={(e) => updateNewToken({ socialTelegram: e.target.value })}
                  inputMode="url"
                  autoComplete="url"
                />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-zinc-400">
          {imageFieldLabel} <span className="text-amber-400/90">*</span>
        </p>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) handleFileSelect(file);
          }}
          className={`relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-10 transition-colors ${
            isDragging
              ? "border-emerald-500/60 bg-emerald-950/20"
              : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500 hover:bg-zinc-900/60"
          }`}
        >
          {uploadedPreview ? (
            <>
              <div
                className={`${TOKEN_LOGO_FRAME} border-2 border-zinc-500/90 bg-zinc-950 ring-1 ring-zinc-800/80`}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={uploadedPreview}
                  alt="Token logo preview"
                  className="h-full w-full object-contain object-center"
                />
              </div>
              <p className="text-sm text-zinc-300">{uploadedFile?.name ?? "Cropped logo"}</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {uploadedPreview ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLogoAdjustCrop();
                    }}
                    className="flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900/80 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                  >
                    <Crop className="h-3 w-3" /> Adjust framing
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={resetLogoSelection}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                className={`${TOKEN_LOGO_FRAME} flex items-center justify-center border-2 border-dashed border-zinc-600/80 bg-zinc-950/40`}
              >
                <ImageIcon className="h-9 w-9 text-zinc-600" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-zinc-200">Select image to upload</p>
                <p className="text-xs text-zinc-500">or drag and drop it here</p>
              </div>
              <button
                type="button"
                className="rounded-xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400"
              >
                Browse
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.gif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
              e.currentTarget.value = "";
            }}
          />
        </div>

        {logoSizeError ? (
          <p className="text-xs text-red-400" role="alert">
            {logoSizeError}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="flex min-w-0 gap-2.5">
            <FileIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-zinc-300">File size and type</p>
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-500">
                <li className="break-words">
                  max {MAX_TOKEN_IMAGE_LABEL.toLowerCase()}. &apos;.jpg&apos;, &apos;.gif&apos; or
                  &apos;.png&apos; recommended
                </li>
              </ul>
            </div>
          </div>
          <div className="flex min-w-0 gap-2.5">
            <ImagePlay className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-zinc-300">Resolution and aspect ratio</p>
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-500">
                <li className="break-words">min. 1000×1000px, 1:1 square recommended</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50">
        <button
          type="button"
          onClick={() => setBannerOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:text-zinc-100"
        >
          <ImagePlay className="h-4 w-4 text-zinc-500" />
          <span>Add banner</span>
          <span className="ml-1 text-xs text-zinc-500">(Optional)</span>
          <span className="ml-auto">
            {bannerOpen ? (
              <ChevronUp className="h-4 w-4 text-zinc-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            )}
          </span>
        </button>

        {bannerOpen && (
          <div className="space-y-4 border-t border-zinc-800 px-4 pb-4 pt-4">
            <div>
              <p className="text-sm font-semibold text-zinc-200">Upload banner</p>
              {showBannerDescription ? (
                <p className="mt-1 text-xs text-zinc-500">
                  Shown on the {assetNoun} page with your {assetNoun} image. The banner is{" "}
                  <span className="text-zinc-400">3:1</span> (wide);{" "}
                  <span className="text-zinc-400">1500×500px</span> is that same ratio at full
                  resolution—other dimensions work, and we crop anything that isn&apos;t 3:1.
                </p>
              ) : null}
            </div>

            <div
              onClick={() => bannerInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsBannerDragging(true);
              }}
              onDragLeave={() => setIsBannerDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsBannerDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) handleBannerSelect(file);
              }}
              className={`relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-8 transition-colors ${
                isBannerDragging
                  ? "border-emerald-500/60 bg-emerald-950/20"
                  : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500 hover:bg-zinc-900/60"
              }`}
            >
              {bannerPreview ? (
                <>
                  <div className="relative aspect-[3/1] w-full max-h-[min(260px,45vh)] overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={bannerPreview}
                      alt="Banner preview"
                      className="h-full w-full object-contain object-center"
                    />
                  </div>
                  <p className="text-sm text-zinc-300">{bannerFile?.name ?? "Banner image"}</p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {bannerFile || bannerPreview ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openBannerAdjustCrop();
                        }}
                        className="flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900/80 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                      >
                        <Crop className="h-3 w-3" /> Adjust framing
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={resetBannerSelection}
                      className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    >
                      <RotateCcw className="h-3 w-3" /> Reset
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 text-zinc-600" />
                  <p className="text-sm font-medium text-zinc-300">Upload file...</p>
                  <button
                    type="button"
                    className="rounded-xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400"
                  >
                    Browse
                  </button>
                </>
              )}
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*,.gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleBannerSelect(f);
                }}
              />
            </div>

            {bannerSizeError ? (
              <p className="text-xs text-red-400" role="alert">
                {bannerSizeError}
              </p>
            ) : null}

            <div className="grid grid-cols-1 gap-4">
              <div className="flex min-w-0 gap-2.5">
                <FileIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-zinc-300">File size and type</p>
                  <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-zinc-500">
                    <li className="break-words whitespace-normal">
                      Image – max {MAX_TOKEN_IMAGE_LABEL}. .jpg, .gif or .png
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex min-w-0 gap-2.5">
                <ImagePlay className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-zinc-300">Shape and pixels</p>
                  <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-zinc-500">
                    <li className="break-words whitespace-normal">
                      <span className="text-zinc-400">3:1</span> aspect (wide rectangle). Recommended
                      export: <span className="text-zinc-400">1500×500px</span>.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2.5 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3">
        <TwoTokensIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
        <p className="text-xs text-zinc-500">
          The new {assetNoun} data (social links, banner, etc) can only be added now, and can&apos;t be
          changed or edited after creation.
        </p>
      </div>

      <BannerCropModal
        open={bannerCropOpen}
        imageSrc={bannerCropSrc}
        onClose={closeBannerCropModal}
        onApply={handleBannerCropApply}
      />

      <ImageCropModal
        open={logoCropOpen}
        imageSrc={logoCropSrc}
        onClose={closeLogoCropModal}
        onApply={handleLogoCropApply}
        aspect={LOGO_ASPECT}
        outputWidth={LOGO_OUTPUT_SIZE}
        outputHeight={LOGO_OUTPUT_SIZE}
        title="Crop token logo"
        description={`Frame matches ${LOGO_OUTPUT_SIZE}×${LOGO_OUTPUT_SIZE}px (1:1). Drag to reposition, use zoom to fit.`}
      />
    </Card>
  );
}
